import { useCallback, useMemo } from "react";
import { IAppointment, IAppointmentFilters, ITherapist } from "../types/appointments.types";
import { useAppointmentsContext } from "../context/AppointmentsContext";

interface UseCalendarColumnsOptions {
  /** Appointments already narrowed by the view's own date range and filters. */
  appointments: IAppointment[];
  filters: IAppointmentFilters;
  /**
   * Whether device mode splits into columns when no device is selected. The daily and weekly boards
   * show every machine because there are only a handful and a free one is what you are looking for;
   * the monthly board would put 3 machines against 31 days, so it stays unsplit until you pick.
   */
  splitDevicesWhenNoSelection: boolean;
}

interface CalendarColumns {
  /** The column axis. Device columns are shaped like therapist ones so grids need no changes. */
  columns: ITherapist[];
  /** True when the board renders one lane per column rather than a single merged lane. */
  isColumnSplit: boolean;
  isDeviceMode: boolean;
  /** Whether an appointment belongs in a given column, by whichever axis is active. */
  matchesColumn: (appointment: IAppointment, columnId: number) => boolean;
  /**
   * Whether an appointment belongs on the board at all under the active axis.
   *
   * In therapist mode the therapist filter is already applied upstream by filterAppointments, so
   * the board only ever receives appointments it should draw. Device mode has no equivalent there,
   * so without this the views receive every appointment: the monthly board (which does not split
   * into columns) drew them all, the totals counted them all, and the weekly proportional layout
   * was handed a shape it never sees in therapist mode.
   */
  matchesAxis: (appointment: IAppointment) => boolean;
}

/**
 * The appointments board's column axis, shared by the daily, weekly and monthly views.
 *
 * All three used to carry an identical copy of this, each one commented "same logic as DailyView",
 * so every change had to land three times — and the device axis needed one genuinely new idea that
 * the copies could not express: splitting into columns and having a therapist filter active used to
 * be the same condition, and for devices they come apart. Keeping that distinction in one place is
 * the point of this hook.
 */
export function useCalendarColumns({
  appointments,
  filters,
  splitDevicesWhenNoSelection,
}: UseCalendarColumnsOptions): CalendarColumns {
  const { therapistsList, deviceColumns, columnMode } = useAppointmentsContext();

  const isDeviceMode = columnMode === "device";
  const selectedDeviceIds = filters.deviceIds ?? [];
  const isTherapistFilterActive = !!filters.therapistIds?.length;

  /** Devices as columns, narrowed to the selection when there is one. */
  const deviceAxis = useMemo<ITherapist[]>(
    () =>
      deviceColumns
        .filter(
          (device) =>
            selectedDeviceIds.length === 0 ||
            selectedDeviceIds.includes(device.deviceContactId),
        )
        .map((device) => ({
          id: device.deviceContactId,
          guid: device.deviceContactGuid,
          name: device.deviceName,
          subtitle: device.treatmentRoomName ?? undefined,
          isActive: true,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceColumns, selectedDeviceIds.join(",")],
  );

  const therapistAxis = useMemo<ITherapist[]>(() => {
    if (isTherapistFilterActive) {
      return therapistsList.filter(
        (therapist) => filters.therapistIds!.includes(therapist.id) && therapist.isActive,
      );
    }

    // Another filter narrowing the board: drop therapists with nothing left to show rather than
    // leaving a wall of empty columns.
    const hasOtherFilters =
      !!filters.fakeCompanyIds?.length ||
      !!filters.fundingSourceIds?.length ||
      !!filters.patientSearch?.trim().length;

    if (hasOtherFilters) {
      const withAppointments = new Set(
        appointments.map((apt) => apt.therapistsId).filter((id) => id !== undefined),
      );
      return therapistsList.filter((t) => t.isActive && withAppointments.has(t.id));
    }

    return therapistsList.filter((t) => t.isActive);
  }, [
    therapistsList,
    isTherapistFilterActive,
    filters.therapistIds,
    filters.fakeCompanyIds,
    filters.fundingSourceIds,
    filters.patientSearch,
    appointments,
  ]);

  const columns = isDeviceMode ? deviceAxis : therapistAxis;

  const isColumnSplit = isDeviceMode
    ? splitDevicesWhenNoSelection || selectedDeviceIds.length > 0
    : isTherapistFilterActive;

  const matchesColumn = useCallback(
    (appointment: IAppointment, columnId: number) =>
      isDeviceMode
        ? appointment.device?.deviceContactId === columnId
        : appointment.therapistsId === columnId,
    [isDeviceMode],
  );

  const matchesAxis = useCallback(
    (appointment: IAppointment) => {
      if (!isDeviceMode) return true;

      const deviceId = appointment.device?.deviceContactId;
      if (!deviceId) return false;

      return selectedDeviceIds.length === 0 || selectedDeviceIds.includes(deviceId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDeviceMode, selectedDeviceIds.join(",")],
  );

  return { columns, isColumnSplit, isDeviceMode, matchesColumn, matchesAxis };
}
